import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import SectionHeader from "@/components/layout/SectionHeader";
import { Star, ArrowRight } from "lucide-react";

export default function PopularDestinations() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/destinations").then(({ data }) => setItems(data)).catch(() => {}); }, []);

  if (!items.length) return null;
  // Use first as big tile, rest as small
  const order = ["dubai", "thailand", "bali", "singapore", "vietnam", "maldives"];
  const sorted = order.map((s) => items.find((i) => i.slug === s)).filter(Boolean);
  const [big, ...rest] = sorted.length ? sorted : items;

  return (
    <section data-testid="popular-destinations" className="container-page py-16 md:py-24">
      <SectionHeader
        kicker="Popular Destinations"
        title="Inspiration for your next escape"
        subtitle="From the futuristic skyline of Dubai to the spiritual beaches of Bali — discover where Indian travellers are heading next."
        cta={
          <Link to="/destinations" data-testid="see-all-destinations" className="text-sm font-semibold text-brand hover:underline inline-flex items-center gap-1 ml-auto">
            See all <ArrowRight size={14} />
          </Link>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 auto-rows-[180px] md:auto-rows-[220px]">
        {big && (
          <Link
            to={`/destinations/${big.slug}`}
            data-testid={`dest-${big.slug}`}
            className="group relative col-span-2 md:col-span-2 row-span-2 rounded-2xl overflow-hidden border border-slate-200"
          >
            <img src={big.image} alt={big.name} className="absolute inset-0 w-full h-full object-cover img-zoom" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/20 to-transparent" />
            <div className="absolute top-4 left-4">
              <span className="px-3 py-1.5 rounded-full bg-accent text-white text-[10px] uppercase tracking-widest font-bold">Trending</span>
            </div>
            <div className="absolute bottom-5 left-5 right-5 text-white">
              <div className="flex items-center gap-1.5 mb-1">
                <Star size={13} className="text-accent fill-accent" />
                <span className="text-xs">{big.rating}</span>
                <span className="text-xs text-white/70">· {big.country}</span>
              </div>
              <h3 className="font-montserrat font-bold text-3xl md:text-4xl">{big.name}</h3>
              <p className="text-sm text-white/85 mt-1 max-w-md">{big.tagline}</p>
              <div className="mt-3 text-xs text-white/80">From <span className="text-accent font-bold text-base">{fmtINR(big.from_price)}</span></div>
            </div>
          </Link>
        )}

        {rest.map((d) => (
          <Link
            key={d.slug}
            to={`/destinations/${d.slug}`}
            data-testid={`dest-${d.slug}`}
            className="group relative rounded-2xl overflow-hidden border border-slate-200"
          >
            <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover img-zoom" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 to-transparent" />
            <div className="absolute bottom-3 left-3 right-3 text-white">
              <h3 className="font-montserrat font-bold text-lg">{d.name}</h3>
              <div className="text-[11px] text-white/85">From <span className="text-accent font-semibold">{fmtINR(d.from_price)}</span></div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
